/**
 * useInvestments Hook
 *
 * React Query hooks for fetching investments with server-side filtering and pagination
 */

import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import { investmentsService } from '../services/investments.service';
import { unwrapResponse } from '../types';

import type { InvestmentFilterParams, PaginationMeta } from '../types';
import type { InvestmentWithRelations } from '@medsuite/models';

export const investmentKeys = {
  all: ['investments'] as const,
  lists: () => [...investmentKeys.all, 'list'] as const,
  list: (filters?: InvestmentFilterParams) => [...investmentKeys.lists(), filters] as const,
  queue: () => [...investmentKeys.all, 'queue'] as const,
  counts: () => [...investmentKeys.all, 'counts'] as const,
  details: () => [...investmentKeys.all, 'detail'] as const,
  detail: (id: number) => [...investmentKeys.details(), id] as const,
  summary: (id: number) => [...investmentKeys.all, 'summary', id] as const,
};

/**
 * Investment counts by status
 */
export interface InvestmentCounts {
  new: number;
  submitted: number;
  approved: number;
  deferred: number;
  rejected: number;
  planned: number;
  in_progress: number;
  complete: number;
  cancelled: number;
  total: number;
  my_investments: number;
}

/**
 * Result from useInvestmentsPaginated hook
 */
export interface PaginatedInvestmentsResult {
  data: InvestmentWithRelations[];
  pagination: PaginationMeta;
}

/**
 * Fetch all investments with optional filters (without pagination info)
 * @deprecated Use useInvestmentsPaginated for proper server-side pagination
 */
export function useInvestments(
  filters?: InvestmentFilterParams,
  options?: Omit<UseQueryOptions<InvestmentWithRelations[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<InvestmentWithRelations[], Error>({
    queryKey: investmentKeys.list(filters),
    queryFn: async () => {
      const response = await investmentsService.getAll(filters);
      // API returns paginated shape: {data: [...], pagination: {...}}
      const responseData = response.data as { data?: InvestmentWithRelations[] };
      return responseData.data ?? (Array.isArray(responseData) ? responseData : []);
    },
    ...options,
  });
}

/**
 * Fetch investments with pagination info for server-side filtering
 */
export function useInvestmentsPaginated(
  filters?: InvestmentFilterParams,
  options?: Omit<UseQueryOptions<PaginatedInvestmentsResult, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<PaginatedInvestmentsResult, Error>({
    queryKey: investmentKeys.list(filters),
    queryFn: async () => {
      const response = await investmentsService.getAll(filters);
      const responseData = response.data as { data?: InvestmentWithRelations[]; pagination?: PaginationMeta };

      // Handle the API response which has shape: { data: [...], pagination: {...} }
      if (responseData.data && responseData.pagination) {
        return {
          data: responseData.data,
          pagination: responseData.pagination,
        };
      }

      // Fallback for legacy response shape
      return {
        data: Array.isArray(responseData) ? responseData : [],
        pagination: {
          page: filters?.page || 1,
          limit: filters?.limit || 25,
          total: Array.isArray(responseData) ? responseData.length : 0,
          totalPages: 1,
          hasNext: false,
          hasPrev: false,
        },
      };
    },
    ...options,
  });
}

/**
 * Fetch investment counts by status
 * Uses the queue endpoint which returns summary counts
 */
export function useInvestmentCounts(
  options?: Omit<UseQueryOptions<InvestmentCounts, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<InvestmentCounts, Error>({
    queryKey: investmentKeys.counts(),
    queryFn: async () => {
      const response = await investmentsService.getQueue();
      // Extract summary from the response: {success: true, data: {queue: {...}, summary: {...}}}
      const data = unwrapResponse<{ summary?: InvestmentCounts; queue?: InvestmentWithRelations[] }>(response.data);
      return (
        data.summary || {
          new: 0,
          submitted: 0,
          approved: 0,
          deferred: 0,
          rejected: 0,
          planned: 0,
          in_progress: 0,
          complete: 0,
          cancelled: 0,
          total: 0,
          my_investments: 0,
        }
      );
    },
    // Always refetch counts when navigating back to ensure fresh data
    staleTime: 0,
    refetchOnMount: 'always',
    ...options,
  });
}

/**
 * Fetch approval queue (investments with status 'submitted')
 */
export function useInvestmentQueue(
  options?: Omit<UseQueryOptions<InvestmentWithRelations[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<InvestmentWithRelations[], Error>({
    queryKey: investmentKeys.queue(),
    queryFn: async () => {
      const response = await investmentsService.getQueue();
      // Extract data from success wrapper: {success: true, data: {queue: {...}, summary: {...}}, meta: {}}
      const data = unwrapResponse<{ queue?: InvestmentWithRelations[]; summary?: InvestmentCounts }>(response.data);
      // Return the queue data which contains investments grouped by status
      return data.queue || [];
    },
    ...options,
  });
}
